import { useCallback, useEffect, useRef, useState } from 'react';

// Real user interaction only -- background polling/API activity must never
// reset this, or an idle user with a live-refreshing dashboard would never
// time out.
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'wheel', 'touchstart', 'scroll'];
const LAST_ACTIVITY_STORAGE_KEY = 'alphamizer_last_activity_at';
const WARNING_WINDOW_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 1000;

/**
 * Tracks user inactivity and warns/logs out after `timeoutMinutes`.
 *
 * lastActivityAt is kept in localStorage (not component state) so every open
 * tab of the app shares one idle clock -- activity in one tab resets it for
 * all of them, matching how Firebase's own browserLocalPersistence already
 * propagates sign-out across tabs.
 *
 * timeoutMinutes === null (the "Never" choice) disables the feature entirely.
 */
export function useIdleLogout({ timeoutMinutes, onLogout, enabled = true }) {
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const warningActiveRef = useRef(false);

  const recordActivity = useCallback(() => {
    try {
      window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
    } catch (_) {
      // Ignore storage write errors (private browsing, quota, etc.) --
      // worst case the timer resets from this tab's own state next tick.
    }
    if (warningActiveRef.current) {
      warningActiveRef.current = false;
      setSecondsRemaining(null);
    }
  }, []);

  // Passive listener wrapper -- while the warning modal is open the user
  // must *click* "Stay logged in" to reset the timer. If we let mousemove /
  // keydown / scroll etc. reset it, moving the cursor toward the button
  // would dismiss the modal before the click landed.
  const handlePassiveActivity = useCallback(() => {
    if (warningActiveRef.current) return;
    recordActivity();
  }, [recordActivity]);

  const readLastActivity = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
      const parsed = raw ? Number(raw) : NaN;
      return Number.isFinite(parsed) ? parsed : Date.now();
    } catch (_) {
      return Date.now();
    }
  }, []);

  useEffect(() => {
    if (!enabled || !timeoutMinutes) {
      warningActiveRef.current = false;
      setSecondsRemaining(null);
      return undefined;
    }

    recordActivity();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handlePassiveActivity, { passive: true });
    });

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const intervalId = window.setInterval(() => {
      const remainingMs = timeoutMs - (Date.now() - readLastActivity());

      if (remainingMs <= 0) {
        warningActiveRef.current = false;
        setSecondsRemaining(null);
        onLogout();
        return;
      }

      if (remainingMs <= WARNING_WINDOW_MS) {
        warningActiveRef.current = true;
        setSecondsRemaining(Math.ceil(remainingMs / 1000));
      } else if (warningActiveRef.current) {
        warningActiveRef.current = false;
        setSecondsRemaining(null);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handlePassiveActivity);
      });
      window.clearInterval(intervalId);
    };
  }, [enabled, timeoutMinutes, onLogout, recordActivity, handlePassiveActivity, readLastActivity]);

  return { secondsRemaining, stayLoggedIn: recordActivity };
}
